/**
 * Character roster — shown after login when the player already has saved
 * AIWARI characters on this storm account. Pick one to load into the
 * world, or create a fresh character via the existing side-select flow.
 *
 * Fetches /aiwari-api/characters/:stormId — that endpoint reads from the
 * `aiwari_characters` table where storm_id matches auth.users.id. The
 * server is the source of truth; the client doesn't try to reconcile a
 * local draft against the DB.
 */

import { useEffect, useState } from 'react';
import { CLASSES, type ClassId } from '../data/classes.js';
import { SIDES, subFactionById, type Side } from '../data/factions.js';
import { FACTIONS, raceById, type Faction, type RaceId, LEGACY_SIDE_TO_FACTION } from '../data/wow-races.js';
import { useAuth } from '../game/auth.js';
import { useUI } from '../state/store.js';

const API_BASE = import.meta.env.DEV ? 'http://localhost:2570' : '/aiwari-api';

interface RosterRow {
  id: string;
  name: string;
  side: Side;
  sub_faction_id: string;
  class_id: ClassId;
  level: number;
  last_login: string | null;
  gender?: 'male' | 'female';
  body_type?: 'slim' | 'medium' | 'broad';
  skin_tone?: string | number;
  hair_style?: number;
  hair_colour?: string;
  race?: RaceId | '';
  faction?: Faction | '';
  /** R232q69: hardcore permadeath flag. Если true — персонаж остаётся в
   *  ростере с пометкой «покинул этот мир навсегда», но играть им нельзя. */
  permadead?: boolean;
  permadead_zone?: string;
  permadead_killer?: string;
  permadead_at?: string | null;
  realm_id?: string;
}

export function CharacterRoster() {
  const userId = useAuth((s) => s.userId);
  const setStage = useUI((s) => s.setStage);
  const setDraft = useUI((s) => s.setDraft);
  const [rows, setRows] = useState<RosterRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    // R232q69: guest mode bug fix.
    // R232q71: + 8s timeout safeguard — hung fetch больше не оставит UI с
    //          вечным «Загрузка…». При timeout показываем error + empty rows
    //          чтобы кнопка «Создать персонажа» всё равно появилась.
    if (!userId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 8000);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/characters/${encodeURIComponent(userId)}`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json() as { rows?: RosterRow[] };
        if (!cancelled) setRows(json.rows ?? []);
      } catch (e) {
        if (cancelled) return;
        const msg = (e as Error).name === 'AbortError'
          ? 'Сервер не ответил за 8 секунд. Попробуй обновить страницу.'
          : (e as Error).message;
        setError(msg);
        setRows([]); // показываем «Создать персонажа» вместо вечной загрузки.
      } finally {
        window.clearTimeout(timer);
      }
    })();
    return () => { cancelled = true; window.clearTimeout(timer); ctrl.abort(); };
  }, [userId]);

  const deleteCharacter = async (row: RosterRow) => {
    if (!userId) return;
    setBusyId(row.id);
    try {
      const res = await fetch(`${API_BASE}/characters/${encodeURIComponent(userId)}/${encodeURIComponent(row.name)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== row.id));
      setConfirmId(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const playCharacter = (row: RosterRow) => {
    // Bridge: if the row has WoW faction/race, use them; otherwise
    // derive from legacy `side` so old characters keep working.
    const faction: Faction = (row.faction || LEGACY_SIDE_TO_FACTION[row.side] || 'alliance') as Faction;
    setDraft({
      name: row.name,
      side: row.side,
      subFactionId: row.sub_faction_id,
      classId: row.class_id,
      faction,
      raceId: (row.race && raceById(row.race)) ? (row.race as RaceId) : null,
      gender: row.gender ?? 'male',
      bodyType: row.body_type ?? 'medium',
      skinTone: typeof row.skin_tone === 'string' ? row.skin_tone : '#F1C7A8',
      hairStyle: row.hair_style ?? 0,
      hairColour: row.hair_colour ?? '#3F2417',
    });
    setStage('world');
  };

  const createNew = () => {
    // Clear character-specific fields so the creator opens at step 1.
    setDraft({
      name: '',
      side: null,
      subFactionId: null,
      classId: null,
      faction: null,
      raceId: null,
    });
    setStage('character-creator');
  };
  void FACTIONS;

  return (
    <div
      className="absolute inset-0 z-50 flex flex-col items-center justify-center p-6 overflow-y-auto"
      style={{
        background: '#0B0907',
        fontFamily: 'EB Garamond, Georgia, serif',
        color: '#E8DCC0',
      }}
    >
      {/* Atmosphere overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(58,40,24,0.18) 0%, rgba(11,9,7,0.0) 60%)',
        }}
      />
      <div className="max-w-3xl w-full relative z-10" style={{ animation: 'fadein 0.6s' }}>
        <div className="text-center mb-10">
          <div
            className="text-[11px] tracking-[0.5em] mb-3"
            style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif', textTransform: 'uppercase' }}
          >
            Выбери своего героя
          </div>
          <h1
            className="leading-none"
            style={{
              fontFamily: 'Cinzel, serif',
              fontWeight: 900,
              fontSize: 'clamp(56px, 8vw, 88px)',
              background: 'linear-gradient(180deg, #F2D58A 0%, #E8C77B 35%, #C9A96A 65%, #8A6B2E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.06em',
              filter: 'drop-shadow(0 4px 14px rgba(201,169,106,0.3))',
            }}
          >
            WORLD OF VIBE CRAFT
          </h1>
          <div
            className="mx-auto mt-3 h-px"
            style={{ width: 200, background: 'linear-gradient(90deg, transparent, #C9A96A, transparent)' }}
          />
          <div
            className="text-[13px] italic mt-3"
            style={{ color: '#C4B391', fontFamily: 'EB Garamond, serif' }}
          >
            Твои герои на этом аккаунте
          </div>
        </div>

        {error && (
          <div className="text-center text-aiwari-danger text-sm mb-4">Ошибка: {error}</div>
        )}

        {rows === null && !error && (
          <div className="text-center text-aiwari-fgMuted text-sm">Загрузка…</div>
        )}

        {rows && rows.length === 0 && (
          <div className="text-center">
            <div className="text-aiwari-fgDim mb-6 text-sm">У тебя ещё нет персонажей. Создадим первого?</div>
            <button
              type="button"
              onClick={createNew}
              className="px-10 py-3 text-base font-bold rounded-xl bg-aiwari-bolt text-aiwari-deep hover:bg-amber-300 transition-all hover:scale-105"
            >
              Создать персонажа →
            </button>
          </div>
        )}

        {rows && rows.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {rows.map((r) => {
                const sub = subFactionById(r.sub_faction_id);
                const cls = CLASSES.find((c) => c.id === r.class_id);
                const isConfirming = confirmId === r.id;
                const isBusy = busyId === r.id;
                return (
                  <div
                    key={r.id}
                    className="rounded-xl p-3 transition group relative overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${cls?.colour ?? '#3A2818'}15, ${SIDES[r.side]?.colour ?? '#3A2818'}15), linear-gradient(180deg, #161210, #0B0907)`,
                      border: `2px solid #5C4520`,
                      boxShadow: `0 0 14px rgba(0,0,0,0.6), inset 0 0 12px rgba(201,169,106,0.10)`,
                    }}
                  >
                    {r.permadead && (
                      <div
                        className="absolute top-0 left-0 right-0 z-20 text-center py-1"
                        style={{
                          background: 'linear-gradient(180deg, rgba(80,12,12,0.95), rgba(40,6,6,0.95))',
                          borderBottom: '1px solid #5C1A1A',
                          fontFamily: 'Spectral SC, serif',
                          color: '#E8C77B',
                          fontSize: 9,
                          letterSpacing: '0.22em',
                          textTransform: 'uppercase',
                        }}
                      >
                        ✦ Покинул этот мир навсегда ✦
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => !r.permadead && playCharacter(r)}
                      disabled={isBusy || !!r.permadead}
                      className={`text-left w-full ${r.permadead ? 'opacity-60 cursor-not-allowed grayscale pt-5' : ''}`}
                      title={r.permadead ? 'Этот герой покинул мир навсегда (Бессмертник)' : ''}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-20 h-20 overflow-hidden flex-shrink-0 relative"
                          style={{ border: `2px solid ${cls?.colour ?? '#C9A96A'}`, boxShadow: 'inset 0 0 8px rgba(0,0,0,0.6)' }}
                        >
                          <img
                            src={`${import.meta.env.BASE_URL}icons/classes/${r.class_id}.png`}
                            alt={cls?.name.ru ?? r.class_id}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className="text-lg truncate"
                            style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, color: '#E8DCC0', letterSpacing: '0.04em' }}
                          >
                            {r.name}
                          </div>
                          <div className="text-[11px] mt-0.5 flex items-center gap-1.5" style={{ fontFamily: 'EB Garamond, serif' }}>
                            <span style={{ color: cls?.colour ?? '#C4B391', fontFamily: 'Cinzel, serif' }}>
                              {cls?.name.ru ?? r.class_id}
                            </span>
                            <span style={{ color: '#5C4520' }}>·</span>
                            <span style={{ color: '#E8C77B', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>УР {r.level}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <img
                              src={`${import.meta.env.BASE_URL}icons/factions/${r.side}.png`}
                              alt={r.side}
                              className="w-4 h-4 object-cover"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                            <span className="text-[10px]" style={{ color: SIDES[r.side]?.colour ?? '#9C8E72', fontFamily: 'Spectral SC, serif', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                              {sub?.name.ru ?? r.sub_faction_id}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                    {/* Heraldic delete sigil — top-right corner. */}
                    {!isConfirming && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmId(r.id); }}
                        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center transition-all hover:scale-110"
                        title="Удалить персонажа"
                        style={{
                          background: 'linear-gradient(180deg, #1F1813, #0B0907)',
                          border: '1px solid #5C4520',
                          color: '#9C8E72',
                          fontFamily: 'EB Garamond, serif',
                          fontSize: 14,
                          lineHeight: 1,
                        }}
                      >
                        ✕
                      </button>
                    )}
                    {isConfirming && (
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center px-4 z-10"
                        style={{
                          background: 'linear-gradient(180deg, rgba(11,9,7,0.96), rgba(22,18,16,0.96))',
                          border: '2px solid #A33232',
                        }}
                      >
                        <div className="text-[10px] mb-1" style={{ fontFamily: 'Spectral SC, serif', color: '#A33232', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                          Окончательное удаление
                        </div>
                        <div className="text-[13px] mb-3 italic text-center" style={{ fontFamily: 'EB Garamond, serif', color: '#C4B391' }}>
                          Стереть «{r.name}» навсегда?
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => deleteCharacter(r)}
                            className="px-4 py-1.5 text-[11px] transition hover:scale-105"
                            style={{
                              background: 'linear-gradient(180deg, #A33232, #5C1A1A)',
                              color: '#E8DCC0',
                              border: '1px solid #A33232',
                              fontFamily: 'Spectral SC, serif',
                              fontWeight: 700,
                              letterSpacing: '0.18em',
                              textTransform: 'uppercase',
                              opacity: isBusy ? 0.5 : 1,
                            }}
                          >
                            {isBusy ? '…' : 'Стереть'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmId(null)}
                            className="px-4 py-1.5 text-[11px] transition hover:scale-105"
                            style={{
                              background: 'linear-gradient(180deg, #1F1813, #0B0907)',
                              color: '#C4B391',
                              border: '1px solid #5C4520',
                              fontFamily: 'Spectral SC, serif',
                              letterSpacing: '0.18em',
                              textTransform: 'uppercase',
                            }}
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-6">
              <button
                type="button"
                onClick={createNew}
                className="text-aiwari-fgDim hover:text-aiwari-fg text-sm border border-aiwari-border rounded-lg px-4 py-2 hover:border-aiwari-fgDim transition"
              >
                + Создать ещё одного персонажа
              </button>
            </div>
          </>
        )}

        <div className="mt-8 text-center text-[11px] text-aiwari-fgMuted font-mono">
          build {__BUILD_ID__}
        </div>
      </div>

      <style>{`
        @keyframes fadein {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
